import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  CreditCard,
  Calendar,
  RefreshCw,
  XCircle,
  Clock,
  AlertTriangle,
  Scale,
  Mail,
  ChevronRight,
  CheckCircle,
  Euro,
  Shield,
  Zap
} from 'lucide-react';
import { PLAN_LIMITS, getPlanDisplayName } from '../config/planFeatures';

const sections = [
  {
    id: 1,
    title: "Piani di Abbonamento Disponibili",
    icon: CreditCard,
    content: `
      <p>Assietica Gest offre i seguenti piani di abbonamento con i relativi limiti operativi:</p>
      
      <h4 class="font-semibold mt-4 mb-2">Tabella Prezzi (IVA inclusa)</h4>
      <table class="w-full border-collapse text-sm">
        <thead>
          <tr class="bg-slate-100">
            <th class="border p-2 text-left">Piano</th>
            <th class="border p-2 text-left">Prezzo Mensile</th>
            <th class="border p-2 text-left">Prezzo Annuale (-10%)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="border p-2"><strong>Free</strong></td>
            <td class="border p-2">Gratuito</td>
            <td class="border p-2">Gratuito</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Premium</strong></td>
            <td class="border p-2">€9,99</td>
            <td class="border p-2">€107,89</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Governance</strong></td>
            <td class="border p-2">€14,99</td>
            <td class="border p-2">€161,89</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Pro</strong></td>
            <td class="border p-2">€24,99</td>
            <td class="border p-2">€269,89</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Enterprise</strong></td>
            <td class="border p-2">€49,99</td>
            <td class="border p-2">€539,89</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Convenzioni Nazionali</strong></td>
            <td class="border p-2" colspan="2">Prezzo personalizzato su richiesta</td>
          </tr>
        </tbody>
      </table>
      <p class="text-xs text-slate-500 mt-2">Tutti i prezzi sono IVA inclusa (22%)</p>
      
      <h4 class="font-semibold mt-6 mb-2">Limiti Operativi per Piano</h4>
      <table class="w-full border-collapse text-sm">
        <thead>
          <tr class="bg-slate-100">
            <th class="border p-2 text-left">Piano</th>
            <th class="border p-2 text-center">Volontari Attivi</th>
            <th class="border p-2 text-center">Soggetti Unici</th>
            <th class="border p-2 text-center">Eventi/Anno</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="border p-2"><strong>Free</strong></td>
            <td class="border p-2 text-center">20</td>
            <td class="border p-2 text-center">30</td>
            <td class="border p-2 text-center">10</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Premium</strong></td>
            <td class="border p-2 text-center">60</td>
            <td class="border p-2 text-center">100</td>
            <td class="border p-2 text-center">50</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Governance</strong></td>
            <td class="border p-2 text-center">100</td>
            <td class="border p-2 text-center">200</td>
            <td class="border p-2 text-center">100</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Pro</strong></td>
            <td class="border p-2 text-center">250</td>
            <td class="border p-2 text-center">500</td>
            <td class="border p-2 text-center">Illimitati</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Enterprise</strong></td>
            <td class="border p-2 text-center">Illimitati</td>
            <td class="border p-2 text-center">Illimitati</td>
            <td class="border p-2 text-center">Illimitati</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Convenzioni</strong></td>
            <td class="border p-2 text-center">Illimitati</td>
            <td class="border p-2 text-center">Illimitati</td>
            <td class="border p-2 text-center">Illimitati</td>
          </tr>
        </tbody>
      </table>
      
      <p class="mt-3 text-sm text-slate-700"><strong>Nota:</strong> Ogni ente ha diritto a <strong>1 solo utente</strong> per l'accesso alla piattaforma, indipendentemente dal piano sottoscritto.</p>
      
      <h4 class="font-semibold mt-6 mb-2">Funzionalità per Piano</h4>
      <table class="w-full border-collapse text-sm mb-4">
        <thead>
          <tr class="bg-slate-100">
            <th class="border p-2 text-left">Piano</th>
            <th class="border p-2 text-left">Funzionalità Incluse</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="border p-2"><strong>Free</strong></td>
            <td class="border p-2">Gestione volontari e soci, eventi, presenze, quote (visualizzazione), Dashboard base, Kit Amministrativo ETS</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Premium</strong></td>
            <td class="border p-2">Free + Comunicazioni Email/WhatsApp, Kit Soci completo, Archivio documenti, Certificati base, Report PDF, Export (RUNTS), Calendario Scadenzario</td>
          </tr>
          <tr class="bg-indigo-50">
            <td class="border p-2"><strong>Governance</strong></td>
            <td class="border p-2">Premium + <strong>Modulo Governance (Organi Sociali, Assemblee, Consiglio Direttivo)</strong>, Rendicontazione economica (35 cat.), Certificati personalizzati, Libri Sociali PDF, Import Massivo (max 200), Schemi Ministeriali (MOD. D, C)</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Pro</strong></td>
            <td class="border p-2">Governance + Rendicontazione completa (120+ cat.), Statistiche avanzate, <strong>Gestione Magazzino e Assegnazioni</strong>, <strong>Gestione Automezzi (assicurazioni, revisioni, manutenzioni)</strong>, Import massivo (max 500), Chatbot AI, Batch certificati, Schemi Ministeriali completi (MOD. A, B)</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Enterprise</strong></td>
            <td class="border p-2">Pro + Volontari e soggetti illimitati, Supporto prioritario via email (supporto@assietica.it)</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Convenzioni</strong></td>
            <td class="border p-2">Enterprise + Multi-ente, Dashboard federazione, Reportistica aggregata, Account manager dedicato, Formazione inclusa</td>
          </tr>
        </tbody>
      </table>
      
      <h4 class="font-semibold mt-6 mb-2">Definizioni</h4>
      <ul class="list-disc list-inside text-sm space-y-1">
        <li><strong>Volontari Attivi:</strong> Numero massimo di volontari con stato "attivo" registrabili contemporaneamente.</li>
        <li><strong>Soggetti Unici:</strong> Numero massimo di anagrafiche totali (volontari + soci + altri soggetti) registrabili.</li>
        <li><strong>Eventi/Anno:</strong> Numero massimo di eventi creabili in un anno solare.</li>
        <li><strong>Modulo Governance:</strong> Funzionalità per la gestione degli Organi Sociali, Assemblee dei Soci e Consiglio Direttivo (disponibile da piano Governance in su).</li>
        <li><strong>Gestione Magazzino:</strong> Inventario articoli (radio, divise, antenne, PC, ecc.) con assegnazione a volontari, traccia consegne e restituzioni (disponibile da piano Pro in su).</li>
        <li><strong>Gestione Automezzi:</strong> Gestione parco veicoli con assicurazioni (frazionamento rate), revisioni, manutenzioni e scadenzario automatico (disponibile da piano Pro in su).</li>
      </ul>
      
      <p class="mt-4 text-sm text-slate-600">I prezzi indicati sono IVA inclusa. Il superamento dei limiti comporta il blocco delle funzionalità correlate fino all'upgrade del piano. Per i dettagli completi delle funzionalità di ciascun piano, consultare la pagina Prezzi.</p>
    `
  },
  {
    id: 2,
    title: "Attivazione dell'Abbonamento",
    icon: Zap,
    content: `
      <p>L'attivazione di un piano a pagamento avviene secondo le seguenti modalità:</p>
      <ul>
        <li><strong>Registrazione:</strong> L'utente si registra gratuitamente e può utilizzare il piano Free</li>
        <li><strong>Upgrade:</strong> L'upgrade a un piano superiore è immediato dopo il pagamento</li>
        <li><strong>Periodo di prova:</strong> È possibile richiedere un periodo di prova di 14 giorni per i piani Premium, Governance e Pro contattando l'assistenza</li>
        <li><strong>Pagamento:</strong> Accettiamo pagamenti tramite carta di credito/debito (Visa, Mastercard, American Express) e bonifico bancario (solo per piani annuali)</li>
      </ul>
      <p class="mt-4">L'abbonamento si considera attivo dal momento della conferma del pagamento. L'utente riceverà email di conferma con i dettagli dell'abbonamento.</p>
    `
  },
  {
    id: 3,
    title: "Durata e Rinnovo",
    icon: RefreshCw,
    content: `
      <p><strong>Durata dell'abbonamento:</strong></p>
      <ul>
        <li><strong>Mensile:</strong> Durata di 30 giorni dalla data di attivazione</li>
        <li><strong>Annuale:</strong> Durata di 365 giorni dalla data di attivazione</li>
      </ul>
      <p class="mt-4"><strong>Rinnovo automatico:</strong></p>
      <p>Gli abbonamenti si rinnovano automaticamente alla scadenza, salvo disdetta. L'addebito avviene:</p>
      <ul>
        <li>Per abbonamenti mensili: il giorno precedente alla scadenza</li>
        <li>Per abbonamenti annuali: 7 giorni prima della scadenza</li>
      </ul>
      <p class="mt-4">L'utente riceverà un promemoria via email 7 giorni prima del rinnovo con l'indicazione dell'importo che sarà addebitato.</p>
      <p class="mt-2">È possibile disattivare il rinnovo automatico in qualsiasi momento dalle Impostazioni del proprio account.</p>
    `
  },
  {
    id: 4,
    title: "Disdetta e Cancellazione",
    icon: XCircle,
    content: `
      <p><strong>Disdetta dell'abbonamento:</strong></p>
      <ul>
        <li>La disdetta può essere effettuata in qualsiasi momento dalle Impostazioni del proprio account</li>
        <li>La disdetta ha effetto alla scadenza del periodo già pagato</li>
        <li>Non sono previsti rimborsi per periodi parzialmente utilizzati</li>
      </ul>
      <p class="mt-4"><strong>Effetti della disdetta:</strong></p>
      <ul>
        <li>Al termine del periodo pagato, l'account passa automaticamente al piano Free</li>
        <li>L'accesso alle funzionalità premium viene disabilitato</li>
        <li>I dati rimangono salvati e accessibili (nei limiti del piano Free)</li>
        <li>I dati eccedenti i limiti del piano Free saranno accessibili in sola lettura per 90 giorni</li>
      </ul>
      <p class="mt-4"><strong>Cancellazione dell'account:</strong></p>
      <p>La cancellazione completa dell'account e di tutti i dati può essere richiesta contattando l'assistenza. La cancellazione è irreversibile.</p>
    `
  },
  {
    id: 5,
    title: "Modifiche al Piano",
    icon: RefreshCw,
    content: `
      <p><strong>Upgrade (passaggio a piano superiore):</strong></p>
      <ul>
        <li>L'upgrade è immediato e attiva subito le nuove funzionalità</li>
        <li>Viene calcolato un credito proporzionale al tempo rimanente del piano precedente</li>
        <li>Il credito viene applicato al primo pagamento del nuovo piano</li>
      </ul>
      <p class="mt-4"><strong>Downgrade (passaggio a piano inferiore):</strong></p>
      <ul>
        <li>Il downgrade ha effetto alla scadenza del periodo corrente</li>
        <li>Non sono previsti rimborsi per il downgrade</li>
        <li>Prima del downgrade, l'utente deve assicurarsi che i propri dati rientrino nei limiti del nuovo piano</li>
        <li>I dati eccedenti i limiti del nuovo piano saranno accessibili in sola lettura</li>
      </ul>
    `
  },
  {
    id: 6,
    title: "Metodi di Pagamento",
    icon: Euro,
    content: `
      <p><strong>Metodi di pagamento accettati:</strong></p>
      <ul>
        <li><strong>Carta di credito/debito:</strong> Visa, Mastercard, American Express</li>
        <li><strong>Bonifico bancario:</strong> Solo per abbonamenti annuali (minimo €99,99)</li>
        <li><strong>SEPA Direct Debit:</strong> Disponibile per abbonamenti annuali Enterprise</li>
      </ul>
      <p class="mt-4"><strong>Sicurezza dei pagamenti:</strong></p>
      <p>I pagamenti sono gestiti tramite <strong>Stripe</strong>, piattaforma certificata PCI-DSS Level 1. Assietica non memorizza né ha accesso ai dati completi delle carte di pagamento.</p>
      <p class="mt-4"><strong>Fatturazione:</strong></p>
      <ul>
        <li>Per ogni pagamento viene emessa fattura elettronica</li>
        <li>Le fatture sono disponibili nella sezione Impostazioni > Fatturazione</li>
        <li>Per la fatturazione è necessario fornire Codice Fiscale o P.IVA e Codice SDI</li>
      </ul>
    `
  },
  {
    id: 7,
    title: "Mancato Pagamento",
    icon: AlertTriangle,
    content: `
      <p>In caso di mancato pagamento alla scadenza:</p>
      <ul>
        <li><strong>Giorno 1-3:</strong> Nuovo tentativo automatico di addebito</li>
        <li><strong>Giorno 4-7:</strong> Notifica via email con richiesta di aggiornamento metodo di pagamento</li>
        <li><strong>Giorno 8-14:</strong> Periodo di grazia - funzionalità premium ancora attive</li>
        <li><strong>Giorno 15+:</strong> Sospensione delle funzionalità premium, downgrade automatico a piano Free</li>
      </ul>
      <p class="mt-4">Durante il periodo di sospensione, i dati rimangono salvati. L'utente può riattivare l'abbonamento in qualsiasi momento regolarizzando il pagamento.</p>
      <p class="mt-2">Dopo <strong>90 giorni</strong> di mancato pagamento senza riattivazione, i dati eccedenti i limiti del piano Free potrebbero essere eliminati, previa notifica.</p>
    `
  },
  {
    id: 8,
    title: "Diritto di Recesso",
    icon: Scale,
    content: `
      <p>Ai sensi dell'art. 52 del Codice del Consumo (D.Lgs. 206/2005), l'utente consumatore ha diritto di recedere dal contratto entro <strong>14 giorni</strong> dalla data di acquisto, senza dover fornire alcuna motivazione.</p>
      <p class="mt-4"><strong>Come esercitare il recesso:</strong></p>
      <ul>
        <li>Inviare comunicazione scritta a <a href="mailto:supporto@assietica.it" class="text-blue-600 hover:underline">supporto@assietica.it</a></li>
        <li>Oppure utilizzare il modulo di recesso disponibile nelle Impostazioni</li>
      </ul>
      <p class="mt-4"><strong>Effetti del recesso:</strong></p>
      <ul>
        <li>Rimborso integrale entro 14 giorni dalla comunicazione di recesso</li>
        <li>Il rimborso avviene con lo stesso metodo di pagamento utilizzato per l'acquisto</li>
        <li>L'account viene riportato al piano Free</li>
      </ul>
      <p class="mt-4"><strong>Eccezione:</strong> Il diritto di recesso non si applica se l'utente ha già usufruito in modo sostanziale del servizio (es. inserimento di più di 50 record, generazione di report, ecc.).</p>
    `
  },
  {
    id: 9,
    title: "Modifiche alle Condizioni",
    icon: Clock,
    content: `
      <p>Assietica si riserva il diritto di modificare le presenti condizioni di abbonamento, inclusi i prezzi, con un preavviso di <strong>30 giorni</strong>.</p>
      <p class="mt-4"><strong>Comunicazione delle modifiche:</strong></p>
      <ul>
        <li>Le modifiche saranno comunicate via email all'indirizzo registrato</li>
        <li>Un avviso sarà visibile nella dashboard dell'applicazione</li>
        <li>Le nuove condizioni saranno pubblicate su questa pagina</li>
      </ul>
      <p class="mt-4"><strong>Effetti delle modifiche:</strong></p>
      <ul>
        <li>Le modifiche ai prezzi si applicano solo ai rinnovi successivi alla data di efficacia</li>
        <li>L'utente che non accetta le nuove condizioni può disdire prima del rinnovo</li>
        <li>Il proseguimento dell'utilizzo dopo la data di efficacia costituisce accettazione</li>
      </ul>
    `
  },
  {
    id: 10,
    title: "Contatti e Assistenza",
    icon: Mail,
    content: `
      <p>Per qualsiasi domanda relativa all'abbonamento, è possibile contattare l'assistenza:</p>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:supporto@assietica.it" class="text-blue-600 hover:underline">supporto@assietica.it</a></li>
        <li><strong>Chat:</strong> Disponibile nella piattaforma per utenti Premium e superiori</li>
        <li><strong>Telefono:</strong> Disponibile per utenti Enterprise (numero dedicato comunicato via email)</li>
      </ul>
      <p class="mt-4"><strong>Orari di assistenza:</strong></p>
      <ul>
        <li>Email: risposta entro 24-48 ore lavorative</li>
        <li>Chat: Lun-Ven 9:00-18:00</li>
        <li>Telefono Enterprise: Lun-Ven 9:00-18:00</li>
      </ul>
      <p class="mt-4 text-sm text-slate-600">Ultimo aggiornamento: Marzo 2026</p>
    `
  }
];

export default function CondizioniAbbonamento() {
  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="condizioni-abbonamento">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0055BB] to-[#003d88] rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <CreditCard className="w-8 h-8" />
          <h1 className="text-2xl font-bold">Condizioni di Abbonamento</h1>
        </div>
        <p className="text-blue-100">
          Termini e condizioni per i piani di abbonamento Assietica Gest
        </p>
      </div>

      {/* Quick Summary Card */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-amber-800 mb-2">Riepilogo Rapido</h3>
              <ul className="text-sm text-amber-700 space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Rinnovo automatico disattivabile in qualsiasi momento</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Diritto di recesso entro 14 giorni con rimborso completo</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Pagamenti sicuri tramite Stripe (PCI-DSS Level 1)</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Dati sempre tuoi: export completo disponibile in ogni momento</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table of Contents */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ChevronRight className="w-5 h-5 text-[#0055BB]" />
            Indice
          </CardTitle>
        </CardHeader>
        <CardContent>
          <nav className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#section-${section.id}`}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors text-sm"
              >
                <section.icon className="w-4 h-4 text-[#0055BB]" />
                <span>{section.id}. {section.title}</span>
              </a>
            ))}
          </nav>
        </CardContent>
      </Card>

      {/* Content Sections */}
      {sections.map((section) => {
        const Icon = section.icon;
        return (
          <Card key={section.id} id={`section-${section.id}`} className="scroll-mt-20">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 bg-[#0055BB]/10 rounded-lg">
                  <Icon className="w-5 h-5 text-[#0055BB]" />
                </div>
                <span>{section.id}. {section.title}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="prose prose-slate max-w-none prose-ul:list-disc prose-ul:pl-5 prose-li:my-1 prose-p:my-2 prose-table:border-collapse"
                dangerouslySetInnerHTML={{ __html: section.content }}
              />
            </CardContent>
          </Card>
        );
      })}

      {/* Footer */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-6">
          <div className="text-center text-sm text-slate-600">
            <p className="mb-2">
              Le presenti Condizioni di Abbonamento sono parte integrante dei{' '}
              <a href="/termini" className="text-[#0055BB] hover:underline">Termini di Utilizzo</a>
              {' '}e dell'
              <a href="/privacy" className="text-[#0055BB] hover:underline">Informativa Privacy</a>.
            </p>
            <p>
              <strong>Assietica Società Cooperativa – Società Benefit</strong>
            </p>
            <p className="mt-2">
              Per domande: <a href="mailto:supporto@assietica.it" className="text-[#0055BB] hover:underline">supporto@assietica.it</a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
